import { apiService } from './api'
import { API_ENDPOINTS, STORAGE_KEYS } from '@/constants'
import { LoginCredentials, RegisterData, User } from '@/types'
import { storage } from '@/utils/storage'

export interface AuthResponse {
  user: User
  token: string
  refreshToken: string
}

class AuthService {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await apiService.post<AuthResponse>(API_ENDPOINTS.AUTH.LOGIN, credentials)
    this.setAuthData(response)
    return response
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await apiService.post<AuthResponse>(API_ENDPOINTS.AUTH.REGISTER, data)
    this.setAuthData(response)
    return response
  }

  async logout(): Promise<void> {
    try {
      await apiService.post(API_ENDPOINTS.AUTH.LOGOUT)
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      this.clearAuthData()
    }
  }

  async getCurrentUser(): Promise<User> {
    return apiService.get<User>(API_ENDPOINTS.AUTH.ME)
  }

  async refreshToken(): Promise<AuthResponse> {
    const refreshToken = storage.get<string>(STORAGE_KEYS.REFRESH_TOKEN)
    if (!refreshToken) {
      throw new Error('No refresh token available')
    }
    const response = await apiService.post<AuthResponse>(API_ENDPOINTS.AUTH.REFRESH, {
      refreshToken,
    })
    this.setAuthData(response)
    return response
  }

  async forgotPassword(email: string): Promise<void> {
    await apiService.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { email })
  }

  async resetPassword(token: string, password: string): Promise<void> {
    await apiService.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, { token, password })
  }

  private setAuthData(data: AuthResponse): void {
    storage.set(STORAGE_KEYS.AUTH_TOKEN, data.token)
    storage.set(STORAGE_KEYS.REFRESH_TOKEN, data.refreshToken)
    storage.set(STORAGE_KEYS.USER, data.user)
  }

  private clearAuthData(): void {
    storage.remove(STORAGE_KEYS.AUTH_TOKEN)
    storage.remove(STORAGE_KEYS.REFRESH_TOKEN)
    storage.remove(STORAGE_KEYS.USER)
  }

  getStoredToken(): string | null {
    return storage.get<string>(STORAGE_KEYS.AUTH_TOKEN)
  }

  getStoredUser(): User | null {
    return storage.get<User>(STORAGE_KEYS.USER)
  }

  isAuthenticated(): boolean {
    return !!this.getStoredToken()
  }
}

export const authService = new AuthService()
